import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../components/ui/Toast';
import { useAuthStore } from '../stores/auth.store';
import { uploadErrorMessage, withinLimit } from '../utils/uploadLimits';

/** Every extract endpoint reports the caller's remaining credits alongside its payload. */
interface ExtractionResult {
  aiCreditsLeft: number;
  premiumAiCreditsLeft: number;
}

interface Options<T extends ExtractionResult> {
  /** Files pre-staged by "Add with AI" — extraction fires for these on mount. */
  initialFiles?: File[];
  extract: (files: File[]) => Promise<T>;
  onExtracted: (result: T) => void;
}

/**
 * The staged-attachments + "fill in with AI" behaviour shared by the flight,
 * hotel and booking forms: keeping files in local state until save, debiting AI
 * credits, turning a 402 into the paywall message, and auto-running extraction
 * when the form opens with files already attached.
 */
export function useDocumentExtraction<T extends ExtractionResult>({
  initialFiles, extract, onExtracted,
}: Options<T>) {
  const { t } = useTranslation();
  const { updateAiCredits, updatePremiumAiCredits } = useAuthStore();

  const [stagedFiles, setStagedFiles] = useState<File[]>(initialFiles ?? []);
  const [extracting, setExtracting] = useState(false);
  // Remounting the <input type="file"> is the only way to clear its selection,
  // so re-picking the same file after removing it fires onChange again.
  const [fileInputKey, setFileInputKey] = useState(0);

  // `extract`/`onExtracted` are re-created every render by the calling form; the
  // ref lets the mount-only effect below reach the current ones without either
  // becoming a dependency that would re-fire extraction on every keystroke.
  const latest = useRef({ extract, onExtracted, stagedFiles });
  latest.current = { extract, onExtracted, stagedFiles };

  async function runExtraction() {
    const files = latest.current.stagedFiles;
    if (files.length === 0) return;

    setExtracting(true);
    try {
      const result = await latest.current.extract(files);
      updateAiCredits(result.aiCreditsLeft);
      updatePremiumAiCredits(result.premiumAiCreditsLeft);
      latest.current.onExtracted(result);
      toast(t('bookings.toast.extractSuccess'), 'success');
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      toast(status === 402 ? t('billing.aiPaywall.error') : t('bookings.toast.extractFailed'), 'error');
    } finally {
      setExtracting(false);
    }
  }

  // "Add with AI" opens the form with the picked file(s) already staged — read
  // them straight away instead of making the user press the button again.
  const autoExtracted = useRef(false);
  useEffect(() => {
    if (!initialFiles?.length || autoExtracted.current) return;
    autoExtracted.current = true;
    runExtraction();
    // Mount only: re-running would re-bill the extraction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function addFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    // Refused here rather than at save time: a ticket staged now is uploaded
    // minutes later, when the toast no longer points at anything the user did.
    const { accepted, errors } = withinLimit(Array.from(files), 'media');
    errors.forEach(message => toast(message, 'error'));
    if (accepted.length > 0) setStagedFiles(prev => [...prev, ...accepted]);
    setFileInputKey(k => k + 1);
  }

  function removeStaged(index: number) {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  }

  return { stagedFiles, addFiles, removeStaged, extracting, runExtraction, fileInputKey };
}

/**
 * Uploads the files staged during editing, once the parent record exists and has
 * an id to attach them to. A file that fails is reported and skipped rather than
 * failing the save that already succeeded.
 */
export async function uploadStagedFiles<D>(
  files: File[],
  upload: (file: File) => Promise<D>,
  existing: D[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): Promise<D[]> {
  let docs = existing;
  for (const file of files) {
    try {
      docs = [...docs, await upload(file)];
    } catch (error) {
      const fallback = t('bookings.toast.uploadFileFailed', { name: file.name });
      toast(uploadErrorMessage(error, file.name, 'media', fallback), 'error');
    }
  }
  return docs;
}
